import React, { useState, useRef, useEffect } from 'react';
import { supabase } from './supabase';
import { Wrench, Bug, Wind, Zap, Droplets, Sparkles, Building2, ArrowRight, ArrowLeft, Check, Paintbrush, Car, Waves, Layers, Dog } from 'lucide-react';
import { nameError } from './validators';

const CompanySignup = ({ user, onComplete }) => {
  const [step, setStep] = useState(1); // 1: Business Type, 2: Company Info, 3: Plan Selection
  const [businessType, setBusinessType] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedTier, setSelectedTier] = useState('pro');
  const [selectedInterval, setSelectedInterval] = useState('weekly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pre-fill first/last name from user metadata (Google OAuth, signup data, etc.)
  useEffect(() => {
    if (!user) return;
    const meta = user.user_metadata || {};
    let first = meta.first_name || meta.given_name || '';
    let last = meta.last_name || meta.family_name || '';
    // Fallback: split full_name if individual names aren't available
    if (!first && !last && (meta.full_name || meta.name)) {
      const parts = (meta.full_name || meta.name).trim().split(/\s+/);
      first = parts[0] || '';
      last = parts.slice(1).join(' ') || '';
    }
    if (first && !firstName) setFirstName(first);
    if (last && !lastName) setLastName(last);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mobile detection
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;

  // Ref for scrolling to continue button
  const continueButtonRef = useRef(null);
  // Guard against duplicate startFreeTrial calls
  const isStartingRef = useRef(false);

  // Business types with their characteristics
  const businessTypes = [
    {
      id: 'garage_door',
      name: 'Garage Door',
      icon: Wrench,
      description: 'Installation, repair & maintenance',
      needsRecurring: false,
      recommendedTier: 'pro'
    },
    {
      id: 'pest_control',
      name: 'Pest Control',
      icon: Bug,
      description: 'Regular treatments & prevention',
      needsRecurring: true,
      recommendedTier: 'team'
    },
    {
      id: 'hvac',
      name: 'HVAC',
      icon: Wind,
      description: 'Heating, cooling & ventilation',
      needsRecurring: true,
      recommendedTier: 'team'
    },
    {
      id: 'electrical',
      name: 'Electrical',
      icon: Zap,
      description: 'Wiring, panels & repairs',
      needsRecurring: false,
      recommendedTier: 'pro'
    },
    {
      id: 'plumbing',
      name: 'Plumbing',
      icon: Droplets,
      description: 'Pipes, fixtures & drains',
      needsRecurring: false,
      recommendedTier: 'pro'
    },
    {
      id: 'painting',
      name: 'Painting',
      icon: Paintbrush,
      description: 'Interior & exterior painting',
      needsRecurring: false,
      recommendedTier: 'pro'
    },
    {
      id: 'cleaning',
      name: 'Cleaning Services',
      icon: Sparkles,
      description: 'Residential & commercial cleaning',
      needsRecurring: true,
      recommendedTier: 'team'
    },
    {
      id: 'carpet_cleaning',
      name: 'Carpet Cleaning',
      icon: Layers,
      description: 'Carpet & upholstery cleaning',
      needsRecurring: true,
      recommendedTier: 'team'
    },
    {
      id: 'mobile_detailing',
      name: 'Mobile Detailing',
      icon: Car,
      description: 'Auto detailing & car wash',
      needsRecurring: true,
      recommendedTier: 'team'
    },
    {
      id: 'pressure_washing',
      name: 'Pressure Washing',
      icon: Waves,
      description: 'Power washing & exterior cleaning',
      needsRecurring: true,
      recommendedTier: 'team'
    },
    {
      id: 'pooper_scooper',
      name: 'Pooper Scooper',
      icon: Dog,
      description: 'Pet waste removal service',
      needsRecurring: true,
      recommendedTier: 'team'
    },
    {
      id: 'other',
      name: 'Other Service',
      icon: Building2,
      description: 'General field service business',
      needsRecurring: false,
      recommendedTier: 'pro'
    }
  ];

  const getSelectedBusinessType = () => businessTypes.find(b => b.id === businessType);

  // Pricing data - same for all business types
  const pricing = {
    free: {
      name: 'Free',
      description: 'Get paid — the essentials',
      features: [
        'Accept customer payments',
        '1 calendar',
        'Quotes & invoicing',
        'Up to 25 customers'
      ],
      weekly: 0,
      monthly: 0,
      annual: 0
    },
    pro: {
      name: 'Pro',
      description: 'Solo owner-operator',
      features: [
        '1 calendar',
        'Quotes & invoicing',
        'Customer management',
        'Email support'
      ],
      weekly: 12.50,
      monthly: 50,
      annual: 500
    },
    team: {
      name: 'Team',
      description: 'Small crew with multiple techs',
      features: [
        'Up to 5 calendars',
        'SMS texting to customers',
        'Tech assignments',
        'Commission tracking',
        'Priority support'
      ],
      weekly: 25,
      monthly: 100,
      annual: 1000
    },
    growth: {
      name: 'Growth',
      description: 'Large operation',
      features: [
        'Unlimited calendars',
        'SMS texting',
        'Advanced reporting',
        'API access',
        'Custom branding',
        'Premium support'
      ],
      weekly: 50,
      monthly: 200,
      annual: 2000
    }
  };

  const handleBusinessTypeSelect = (type) => {
    setBusinessType(type);
    // Auto-select recommended tier based on business type
    const bizType = businessTypes.find(b => b.id === type);
    if (bizType) {
      setSelectedTier(bizType.recommendedTier);
    }
    // Scroll to continue button after selection
    setTimeout(() => {
      continueButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleBusinessTypeSubmit = () => {
    if (businessType) {
      setStep(2);
    }
  };

  const handleCompanySubmit = (e) => {
    e.preventDefault();
    // Collect all validation errors at once so users see everything wrong in one pass
    const errs = [];
    const firstErr = nameError(firstName, 'First name');
    if (firstErr) errs.push(firstErr);
    const lastErr = nameError(lastName, 'Last name');
    if (lastErr) errs.push(lastErr);
    const coName = (companyName || '').trim();
    if (!coName) {
      errs.push('Company name is required');
    } else if (!/[\p{L}\d]/u.test(coName)) {
      errs.push('Company name must contain at least one letter or number');
    } else if (coName.length > 120) {
      errs.push('Company name is too long');
    }

    if (errs.length > 0) {
      setError(errs.join('. '));
      return;
    }
    setError(null);
    setStep(3);
  };

  // Detect test mode from URL parameter capture
  const isTestMode = typeof window !== 'undefined' && localStorage.getItem('test_mode') === 'true';

  const startFreeTrial = async () => {
    if (isStartingRef.current) return;
    isStartingRef.current = true;
    setLoading(true);
    setError(null);
    console.log('[Signup] Starting trial signup for user:', user?.id, isTestMode ? '(TEST MODE)' : '');

    try {
      // Use SECURITY DEFINER function to create company, link user, save profile, and attribute affiliate
      // This bypasses RLS which can fail if the session token isn't fully established after email confirmation
      console.log('[Signup] Creating company via RPC...');

      const maxCalendars = ['pro', 'free'].includes(selectedTier) ? 1 : (selectedTier === 'team' ? 5 : 999);
      const maxUsers = ['pro', 'free'].includes(selectedTier) ? 1 : (selectedTier === 'team' ? 3 : 999);
      const textingEnabled = !['pro', 'free'].includes(selectedTier);
      const bizType = getSelectedBusinessType();
      const recurringBillingEnabled = bizType?.needsRecurring || false;

      // Get affiliate ref from localStorage or cookie
      const getCookie = (name) => {
        const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        return match ? match[2] : null;
      };
      const affiliateRef = localStorage.getItem('affiliate_ref') || getCookie('affiliate_ref');

      const rpcCall = supabase.rpc('create_company_signup', {
        p_name: companyName,
        p_tier: selectedTier,
        p_billing_interval: selectedInterval,
        p_max_calendars: maxCalendars,
        p_max_users: maxUsers,
        p_texting_enabled: textingEnabled,
        p_business_type: businessType,
        p_recurring_billing_enabled: recurringBillingEnabled,
        p_advanced_reporting_enabled: !['pro', 'free'].includes(selectedTier),
        p_api_access_enabled: ['business', 'growth', 'enterprise'].includes(selectedTier),
        p_custom_branding_enabled: ['business', 'growth', 'enterprise'].includes(selectedTier),
        p_first_name: firstName.trim(),
        p_last_name: lastName.trim(),
        p_email: user.email,
        p_affiliate_ref: affiliateRef || null,
        p_is_demo: isTestMode
      });

      // Race against a timeout to prevent indefinite hang
      const { data: result, error: rpcError } = await Promise.race([
        Promise.resolve(rpcCall),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Request timed out. Please refresh the page and try again.')), 15000))
      ]);

      console.log('[Signup] RPC result:', { result, rpcError });

      if (rpcError) {
        console.error('[Signup] RPC error:', rpcError);
        throw new Error('signup_failed');
      }

      const company = result;

      // Test mode: skip Stripe checkout entirely
      if (isTestMode) {
        console.log('[Signup] TEST MODE - skipping Stripe checkout, company:', company);
        localStorage.removeItem('test_mode');
        if (affiliateRef) {
          localStorage.removeItem('affiliate_ref');
          localStorage.removeItem('affiliate_ref_captured_at');
          document.cookie = 'affiliate_ref=; max-age=0; path=/';
        }
        onComplete(company);
        setLoading(false);
        return;
      }

      // Free tier: no Stripe checkout. Activate immediately (no trial expiry) and enter the app.
      if (selectedTier === 'free' && !result?.already_exists) {
        console.log('[Signup] FREE TIER - skipping Stripe checkout, company:', company?.id);
        try {
          await supabase
            .from('companies')
            .update({ subscription_status: 'active', trial_ends_at: null })
            .eq('id', company.id);
        } catch (freeErr) {
          console.warn('[Signup] Free-tier activation update failed (non-fatal):', freeErr?.message);
        }
        if (affiliateRef) {
          localStorage.removeItem('affiliate_ref');
          localStorage.removeItem('affiliate_ref_captured_at');
          document.cookie = 'affiliate_ref=; max-age=0; path=/';
        }
        onComplete(company);
        setLoading(false);
        return;
      }

      // If user already has a company with Stripe setup, they're done
      if (result?.already_exists && result?.stripe_customer_id) {
        console.log('[Signup] User already has company with Stripe, completing...');
        onComplete();
        setLoading(false);
        return;
      }

      // If user already has a company but no Stripe, we need to send them to checkout
      if (result?.already_exists) {
        console.log('[Signup] User has company but no Stripe, redirecting to checkout...');
      } else {
        console.log('[Signup] Company created successfully:', company?.id);
        // Clear the stored referral code after use (only on new company creation)
        if (affiliateRef) {
          localStorage.removeItem('affiliate_ref');
          localStorage.removeItem('affiliate_ref_captured_at');
          document.cookie = 'affiliate_ref=; max-age=0; path=/';
        }
      }

      // Redirect to Stripe Checkout to collect payment info
      console.log('[Signup] Calling checkout API...');
      const response = await fetch('/api/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          companyId: company.id,
          tier: company.tier || selectedTier,
          interval: company.billing_interval || selectedInterval,
          userEmail: user.email
        }),
      });

      console.log('[Signup] Checkout API response status:', response.status);
      const responseText = await response.text();
      console.log('[Signup] Checkout API response:', responseText);
      let checkoutData;
      try {
        checkoutData = JSON.parse(responseText);
      } catch (parseErr) {
        throw new Error('Failed to connect to payment processor. Please try again.');
      }

      if (!response.ok) {
        throw new Error(checkoutData.error || 'Failed to set up payment. Please try again.');
      }

      if (checkoutData.url) {
        // Redirect to Stripe Checkout via session URL
        console.log('[Signup] Redirecting to Stripe checkout...');
        window.location.href = checkoutData.url;
        return;
      } else {
        throw new Error('Failed to set up payment. Please try again.');
      }

    } catch (err) {
      console.error('[Signup] Error:', err);
      // Show user-friendly error - never expose raw database errors
      setError(err.message && !err.message.includes('signup_failed')
        ? err.message
        : 'Something went wrong setting up your account. Please try again, or contact support if the problem continues.');
      setLoading(false);
      isStartingRef.current = false;
    }
  };

  return (
    <div style={{
      height: '100vh',
      height: '100dvh',
      background: '#0a0a0a',
      padding: isMobile ? '1rem' : '2rem',
      paddingTop: isMobile ? 'max(1rem, env(safe-area-inset-top))' : '2rem',
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
      touchAction: 'pan-y',
      boxSizing: 'border-box'
    }}>
      <div style={{
        background: '#1a1a1a',
        borderRadius: '12px',
        maxWidth: '800px',
        width: '100%',
        padding: isMobile ? '1.25rem' : '3rem',
        border: '1px solid #3a3a3a',
        margin: '0 auto',
        marginBottom: '2rem'
      }}>
        {/* Test Mode Banner */}
        {isTestMode && (
          <div style={{
            background: '#2a1a2a',
            border: '2px solid #e040fb',
            borderRadius: '8px',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}>
            <span style={{
              background: '#e040fb',
              color: '#fff',
              fontSize: '0.7rem',
              padding: '0.2rem 0.6rem',
              borderRadius: '4px',
              fontWeight: 'bold',
              letterSpacing: '0.05em'
            }}>
              TEST MODE
            </span>
            <span style={{ color: '#e040fb', fontSize: '0.9rem' }}>
              Stripe checkout will be skipped
            </span>
          </div>
        )}

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: isMobile ? '1.25rem' : '2rem' }}>
          <img
            src="/WorkVan-logo-dark.png"
            alt="Work Van"
            style={{
              height: isMobile ? '80px' : '120px',
              width: 'auto',
              marginBottom: '1.5rem'
            }}
          />
          <h1 style={{
            fontSize: isMobile ? '1.5rem' : '2rem',
            fontWeight: 'bold',
            color: '#e0e0e0',
            marginBottom: '0.5rem'
          }}>
            Welcome to Work Van!
          </h1>
          <p style={{ color: '#888', fontSize: isMobile ? '0.95rem' : '1.1rem' }}>
            {step === 1 && "What type of business are you?"}
            {step === 2 && "Tell us about your company"}
            {step === 3 && "Choose the plan that fits your needs"}
          </p>
        </div>

        {/* Progress Steps */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: isMobile ? '0.25rem' : '0.5rem',
          marginBottom: isMobile ? '1.5rem' : '3rem',
          flexWrap: 'wrap'
        }}>
          {[
            { num: 1, label: 'Business Type' },
            { num: 2, label: 'Company Info' },
            { num: 3, label: 'Choose Plan' }
          ].map(({ num, label }) => (
            <div key={num} style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '0.25rem' : '0.5rem'
            }}>
              <div style={{
                width: isMobile ? '28px' : '32px',
                height: isMobile ? '28px' : '32px',
                borderRadius: '50%',
                background: step >= num ? '#F05A28' : '#2a2a2a',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: isMobile ? '0.8rem' : '0.9rem'
              }}>
                {step > num ? <Check size={isMobile ? 14 : 16} /> : num}
              </div>
              {!isMobile && (
                <span style={{
                  color: step >= num ? '#e0e0e0' : '#666',
                  fontWeight: step === num ? 'bold' : 'normal',
                  fontSize: '0.9rem'
                }}>
                  {label}
                </span>
              )}
              {num < 3 && (
                <ArrowRight size={isMobile ? 12 : 16} style={{ color: '#666', marginLeft: isMobile ? '0' : '0.25rem' }} />
              )}
            </div>
          ))}
        </div>

        {error && (
          <div style={{
            background: '#2a1a1a',
            border: '1px solid #e74c3c',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '2rem',
            color: '#e74c3c'
          }}>
            <p style={{ margin: '0 0 0.75rem' }}>{error}</p>
            <button
              onClick={async () => {
                await supabase.auth.signOut();
                window.location.href = '/';
              }}
              style={{
                background: 'transparent',
                border: '1px solid #e74c3c',
                color: '#e74c3c',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.85rem'
              }}
            >
              Sign out and try again
            </button>
          </div>
        )}

        {/* Step 1: Business Type Selection */}
        {step === 1 && (
          <div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {businessTypes.map((biz) => {
                const IconComponent = biz.icon;
                const isSelected = businessType === biz.id;
                return (
                  <div
                    key={biz.id}
                    onClick={() => handleBusinessTypeSelect(biz.id)}
                    style={{
                      background: isSelected ? '#2a2a2a' : '#1a1a1a',
                      border: isSelected ? '2px solid #F05A28' : '1px solid #3a3a3a',
                      borderRadius: '12px',
                      padding: '1.25rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      textAlign: 'center',
                      minHeight: '120px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <IconComponent
                      size={32}
                      style={{ color: isSelected ? '#F05A28' : '#888' }}
                    />
                    <h4 style={{
                      color: isSelected ? '#e0e0e0' : '#aaa',
                      fontWeight: 'bold',
                      fontSize: '0.95rem',
                      margin: 0
                    }}>
                      {biz.name}
                    </h4>
                    <p style={{
                      color: '#666',
                      fontSize: '0.75rem',
                      margin: 0
                    }}>
                      {biz.description}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Explanation box based on selection */}
            {businessType && (
              <div style={{
                background: getSelectedBusinessType()?.needsRecurring ? '#1a2a1a' : '#1a1a2a',
                border: `1px solid ${getSelectedBusinessType()?.needsRecurring ? '#4ecca3' : '#3498db'}`,
                borderRadius: '8px',
                padding: '1.25rem',
                marginBottom: '1.5rem'
              }}>
                {getSelectedBusinessType()?.needsRecurring ? (
                  <>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.75rem'
                    }}>
                      <span style={{
                        background: '#4ecca3',
                        color: '#0a0a0a',
                        fontSize: '0.7rem',
                        padding: '0.2rem 0.5rem',
                        borderRadius: '4px',
                        fontWeight: 'bold'
                      }}>
                        RECURRING SERVICE
                      </span>
                    </div>
                    <p style={{ color: '#e0e0e0', fontSize: '0.95rem', margin: '0 0 0.75rem 0', lineHeight: '1.5' }}>
                      <strong>You see the same customers regularly</strong> - weekly, monthly, or on a schedule.
                    </p>
                    <p style={{ color: '#888', fontSize: '0.85rem', margin: 0, lineHeight: '1.5' }}>
                      Examples: Monthly pest treatments, quarterly HVAC maintenance, weekly cleaning service.
                      We'll help you track recurring visits and automate scheduling. You can still schedule one-time jobs too!
                    </p>
                  </>
                ) : (
                  <>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.75rem'
                    }}>
                      <span style={{
                        background: '#3498db',
                        color: '#fff',
                        fontSize: '0.7rem',
                        padding: '0.2rem 0.5rem',
                        borderRadius: '4px',
                        fontWeight: 'bold'
                      }}>
                        ONE-TIME SERVICE
                      </span>
                    </div>
                    <p style={{ color: '#e0e0e0', fontSize: '0.95rem', margin: '0 0 0.75rem 0', lineHeight: '1.5' }}>
                      <strong>You do the job, get paid, and move on</strong> - most customers are one-time or as-needed.
                    </p>
                    <p style={{ color: '#888', fontSize: '0.85rem', margin: 0, lineHeight: '1.5' }}>
                      Examples: Garage door install, electrical repair, emergency plumbing.
                      We'll help you quote fast, schedule the job, and get paid.
                    </p>
                  </>
                )}
              </div>
            )}

            <button
              ref={continueButtonRef}
              onClick={handleBusinessTypeSubmit}
              disabled={!businessType}
              style={{
                width: '100%',
                padding: '1rem',
                background: businessType ? '#F05A28' : '#3a3a3a',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: businessType ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              Continue <ArrowRight size={20} />
            </button>
          </div>
        )}

        {/* Step 2: Company Info */}
        {step === 2 && (
          <form onSubmit={handleCompanySubmit}>
            {/* Show selected business type */}
            {businessType && (
              <div style={{
                background: '#2a2a2a',
                borderRadius: '8px',
                padding: '0.75rem 1rem',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                {(() => {
                  const biz = getSelectedBusinessType();
                  const IconComponent = biz?.icon;
                  return IconComponent ? (
                    <>
                      <IconComponent size={20} style={{ color: '#F05A28' }} />
                      <span style={{ color: '#e0e0e0' }}>{biz.name} Business</span>
                    </>
                  ) : null;
                })()}
              </div>
            )}

            {/* Name fields */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#e0e0e0',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                }}>
                  First Name *
                </label>
                <input
                  type="text"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: '#2a2a2a',
                    border: '1px solid #3a3a3a',
                    borderRadius: '8px',
                    color: '#e0e0e0',
                    fontSize: '1rem'
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#e0e0e0',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                }}>
                  Last Name *
                </label>
                <input
                  type="text"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: '#2a2a2a',
                    border: '1px solid #3a3a3a',
                    borderRadius: '8px',
                    color: '#e0e0e0',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#e0e0e0',
                fontWeight: 'bold',
                fontSize: '1.1rem'
              }}>
                Company Name *
              </label>
              <input
                type="text"
                required
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder={`e.g., ${firstName || 'Josh'}'s ${getSelectedBusinessType()?.name || 'Service'} Co.`}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: '#2a2a2a',
                  border: '1px solid #3a3a3a',
                  borderRadius: '8px',
                  color: '#e0e0e0',
                  fontSize: '1rem'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                type="button"
                onClick={() => setStep(1)}
                style={{
                  flex: 1,
                  padding: '1rem',
                  background: '#2a2a2a',
                  color: '#e0e0e0',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <ArrowLeft size={18} /> Back
              </button>
              <button
                type="submit"
                disabled={!firstName.trim() || !lastName.trim() || !companyName.trim()}
                style={{
                  flex: 2,
                  padding: '1rem',
                  background: (firstName.trim() && lastName.trim() && companyName.trim()) ? '#F05A28' : '#3a3a3a',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  cursor: (firstName.trim() && lastName.trim() && companyName.trim()) ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                Continue <ArrowRight size={20} />
              </button>
            </div>
          </form>
        )}

        {/* Step 3: Plan Selection */}
        {step === 3 && (
          <div>
            {/* Context banner showing selections */}
            <div style={{
              background: '#2a2a2a',
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '0.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {(() => {
                  const biz = getSelectedBusinessType();
                  const IconComponent = biz?.icon;
                  return IconComponent ? <IconComponent size={18} style={{ color: '#F05A28' }} /> : null;
                })()}
                <span style={{ color: '#e0e0e0', fontWeight: 'bold' }}>{companyName}</span>
              </div>
            </div>

            {/* Billing Interval Toggle */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: isMobile ? '0.5rem' : '1rem',
              marginBottom: isMobile ? '1.25rem' : '2rem',
              flexWrap: isMobile ? 'wrap' : 'nowrap'
            }}>
              {['weekly', 'monthly', 'annual'].map(interval => (
                <button
                  key={interval}
                  onClick={() => setSelectedInterval(interval)}
                  style={{
                    padding: isMobile ? '0.6rem 1rem' : '0.75rem 1.5rem',
                    background: selectedInterval === interval ? '#F05A28' : '#2a2a2a',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                    fontSize: isMobile ? '0.85rem' : '1rem',
                    flex: isMobile ? '1 1 auto' : 'none',
                    minWidth: isMobile ? '80px' : 'auto'
                  }}
                >
                  {interval}
                  {interval === 'annual' && (
                    <span style={{
                      marginLeft: '0.5rem',
                      fontSize: isMobile ? '0.7rem' : '0.8rem',
                      color: '#4ecca3',
                      display: isMobile ? 'block' : 'inline'
                    }}>
                      Save 17%
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Tier Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: isMobile ? '1rem' : '1.5rem',
              marginBottom: isMobile ? '1.25rem' : '2rem'
            }}>
              {Object.entries(pricing).map(([tier, info]) => {
                const isRecommended = getSelectedBusinessType()?.recommendedTier === tier;
                return (
                  <div
                    key={tier}
                    onClick={() => setSelectedTier(tier)}
                    style={{
                      background: selectedTier === tier ? '#2a2a2a' : '#1a1a1a',
                      border: selectedTier === tier ? '2px solid #F05A28' : (isRecommended ? '2px solid #4ecca3' : '1px solid #3a3a3a'),
                      borderRadius: '12px',
                      padding: isMobile ? '1rem' : '1.5rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}
                  >
                    {isRecommended && (
                      <div style={{
                        position: 'absolute',
                        top: '-12px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#4ecca3',
                        color: '#0a0a0a',
                        fontSize: '0.7rem',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontWeight: 'bold'
                      }}>
                        Recommended
                      </div>
                    )}
                    <div style={{ display: isMobile ? 'flex' : 'block', alignItems: 'flex-start', gap: isMobile ? '1rem' : 0 }}>
                      <div style={{ flex: isMobile ? 1 : 'none' }}>
                        <h3 style={{
                          fontSize: isMobile ? '1.1rem' : '1.3rem',
                          fontWeight: 'bold',
                          color: '#e0e0e0',
                          marginBottom: '0.25rem',
                          marginTop: isRecommended ? '0.5rem' : 0
                        }}>
                          {info.name}
                        </h3>
                        <p style={{
                          fontSize: isMobile ? '1.5rem' : '2rem',
                          fontWeight: 'bold',
                          color: '#F05A28',
                          marginBottom: '0.25rem'
                        }}>
                          ${info[selectedInterval]}
                          <span style={{ fontSize: isMobile ? '0.8rem' : '0.9rem', color: '#888' }}>
                            /{selectedInterval === 'annual' ? 'year' : selectedInterval.slice(0, -2)}
                          </span>
                        </p>
                        {tier === 'pro' && selectedInterval === 'weekly' && (
                          <p style={{
                            color: '#4ecca3',
                            fontSize: isMobile ? '0.75rem' : '0.8rem',
                            marginBottom: '0.25rem',
                            fontStyle: 'italic'
                          }}>
                            Cheaper than a combo meal
                          </p>
                        )}
                        <p style={{ color: '#888', fontSize: isMobile ? '0.8rem' : '0.9rem', marginBottom: '0.25rem' }}>
                          {info.description}
                        </p>
                        <p style={{ color: '#666', fontSize: '0.75rem', marginBottom: isMobile ? 0 : '1rem' }}>
                          {tier === 'pro' ? '1 user' : tier === 'team' ? '3 users included' : 'Unlimited users'}
                        </p>
                      </div>
                      {isMobile && selectedTier === tier && (
                        <div style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          background: '#F05A28',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <Check size={16} color="#fff" />
                        </div>
                      )}
                    </div>
                    {(!isMobile || selectedTier === tier) && (
                      <ul style={{ listStyle: 'none', padding: 0, margin: isMobile ? '0.75rem 0 0 0' : 0 }}>
                        {info.features.map((feature, idx) => (
                          <li key={idx} style={{
                            color: '#e0e0e0',
                            marginBottom: '0.35rem',
                            fontSize: isMobile ? '0.8rem' : '0.9rem'
                          }}>
                            <Check size={isMobile ? 12 : 14} style={{ color: '#4ecca3', marginRight: '0.4rem' }} />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Trial Notice */}
            <div style={{
              background: '#2a2a2a',
              border: '1px solid #4ecca3',
              borderRadius: '8px',
              padding: isMobile ? '0.75rem' : '1rem',
              marginBottom: isMobile ? '1.25rem' : '2rem',
              textAlign: 'center'
            }}>
              <span style={{ color: '#4ecca3', fontWeight: 'bold', fontSize: isMobile ? '0.9rem' : '1rem' }}>
                14-day free trial
              </span>
              <p style={{ color: '#888', fontSize: isMobile ? '0.8rem' : '0.9rem', margin: '0.5rem 0 0 0' }}>
                You won't be charged until your trial ends. Cancel anytime.
              </p>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: isMobile ? '0.75rem' : '1rem', flexDirection: isMobile ? 'column' : 'row' }}>
              <button
                onClick={() => setStep(2)}
                disabled={loading}
                style={{
                  flex: isMobile ? 'none' : 1,
                  padding: isMobile ? '0.875rem' : '1rem',
                  background: '#2a2a2a',
                  color: '#e0e0e0',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  order: isMobile ? 2 : 1
                }}
              >
                <ArrowLeft size={18} /> Back
              </button>
              <button
                onClick={startFreeTrial}
                disabled={loading}
                style={{
                  flex: isMobile ? 'none' : 2,
                  padding: isMobile ? '1rem' : '1rem',
                  background: '#F05A28',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: isMobile ? '1rem' : '1rem',
                  fontWeight: 'bold',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  order: isMobile ? 1 : 2
                }}
              >
                {loading ? 'Setting up...' : <>Next <ArrowRight size={18} /></>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanySignup;
